import React from "react";
import { DateTime } from "luxon";
import OverviewView, { type MessengerChannel, type PersonalLink, type StatusPanel } from "./OverviewView";
import { useBookingDraft } from "../BookingDraftContext";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { getStaffRegistry } from "@/lib/staff/staffRegistrySingleton";
import { useServiceCatalog } from "@/hooks/services/useServiceCatalog";
import { formatDurationSeconds } from "@/lib/utils";
import { useCreatePublicRecord } from "../hooks/useCreatePublicRecord";
import { getStoredClientAuth, isClientLoggedIn } from "../../client/ClientAuthContext";
import { useTranslation } from "react-i18next";
import { getLuxonLocale } from "@/lib/i18n/dateLocales";
import { displayPrice, priceLabel } from "@/lib/services/masterPrice";
import { validateName, validatePhone, validateOptionalEmail, contactErrorMessage, phoneCodeExample } from "@/lib/validation/contact";
import { calendarLinksOf } from "../../../lib/calendarLinks";

/**
 * Container for OverviewView
 * - reads BookingDraft
 * - resolves specialist name via StaffRegistry
 * - resolves services (name, price, duration) via service catalog snapshot
 * - formats date/time in salon tz
 * - handles contact form validation and submission
 */

/** /public/company/settings messenger block (README §10.1): {enabled, phone,
 *  channels: [{id, auto, kind?, label?}]} in the salon's order; older shard bundles
 *  sent {auto, channels: string[]}. */
function normalizeMessengerChannels(block: unknown): MessengerChannel[] {
  const b = (block ?? {}) as { auto?: boolean; channels?: unknown };
  const list = Array.isArray(b.channels) ? b.channels : [];
  return list.map((c) => {
    if (typeof c === "string") return { id: c, auto: Boolean(b.auto) };
    const e = c as { id: string; auto?: boolean; kind?: string; label?: string };
    return {
      id: String(e.id),
      auto: Boolean(e.auto),
      kind: e.kind === "own" || e.kind === "bot" || e.kind === "official" ? e.kind : undefined,
      label: typeof e.label === "string" && e.label ? e.label : undefined,
    };
  });
}

export default function OverviewViewContainer({
  onBack,
  onEditSpecialist,
  onEditServices,
  onEditDateTime,
  onRestart,
  onOpenClientDashboard,
}: {
  onBack: () => void;
  onEditSpecialist: () => void;
  onEditServices: () => void;
  onEditDateTime: () => void;
  onRestart: () => void;
  onOpenClientDashboard?: () => void;
}) {
  const { t, i18n } = useTranslation();

  const { draft, actions } = useBookingDraft();
  const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";
  const companySettings = getCompanyRegistry().getSettings();
  const currency = companySettings?.currency_short_title ?? "BYN";

  // The salon's channels in its order (Settings › Messenger). A connected one lets the
  // client confirm in the chat, so the first of them is preselected once (owner,
  // 2026-10-21); with none the phone call stays the default, unless the salon offers no
  // phone call — then the first channel.
  const messengersOn = companySettings?.messenger?.enabled !== false;
  const messengerChannels = messengersOn ? normalizeMessengerChannels(companySettings?.messenger) : [];
  const phoneOffered = companySettings?.messenger?.phone !== false || !messengerChannels.length;
  const preset = messengerChannels.find((c) => c.auto) ?? (phoneOffered ? undefined : messengerChannels[0]);
  React.useEffect(() => {
    if (draft.confirmPreset) return;
    if (preset) actions.presetConfirm(2, preset.id);
    else actions.presetConfirm(1);
    // the draft and its actions change identity every render; the flag runs it once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.confirmPreset, preset?.id]);
  // a channel the salon has switched off since the draft was made falls back
  const pickedChannel = messengerChannels.find((c) => c.id === draft.messengerChannel) ?? preset ?? messengerChannels[0];
  const language = i18n.resolvedLanguage || i18n.language || "en";
  const luxonLocale = getLuxonLocale(language);

  // Use the same service snapshot the Services container uses
  const { data: snap } = useServiceCatalog({ enabled: true });

  // Specialist name + avatar
  let specialistName = t("widget.specialists.any_specialist", "Any specialist");
  let specialistAvatar: string | undefined = undefined;
  if (draft.specialistId && draft.specialistId !== "any") {
    const staffById = getStaffRegistry().getStaffByIdMap();
    const idNum = Number(draft.specialistId);
    const staff = staffById[idNum];
    if (staff) {
      specialistName = staff.name;
      specialistAvatar = staff.avatar || undefined;
    } else {
      specialistName = t("widget.common.specialist", "Specialist");
    }
  }

  const services = React.useMemo(() => {
    const ids = draft.serviceIds ?? [];
    if (!ids.length || !snap) {
      return ids.map((id) => ({ id, name: t("widget.common.service", "Service"), price: "—", duration: "—" }));
    }

    // Build a lookup from snap.services
    const svcById = new Map<string, any>();
    Object.values(snap.services || {}).forEach((s: any) => svcById.set(String(s.id), s));

    const masterId = draft.specialistId && draft.specialistId !== "any" ? Number(draft.specialistId) : null;
    const fmtNum = (v: number) => new Intl.NumberFormat(language).format(v);
    const fromLabel = (price: string) => t("widget.services.price_from", "from {{price}}", { price });

    return ids.map((id) => {
      const s = svcById.get(id);
      if (!s) {
        return { id, name: t("widget.common.service", "Service"), price: "—", duration: "—" };
      }
      // The chosen master's own price (per-master prices); "any master" shows the
      // service price, or "from" the cheapest master when the service price is 0.
      const shown = displayPrice(s, masterId);
      const priceStr = priceLabel(shown, fmtNum, fromLabel) + " " + currency;
      // Prefer master-specific duration if present (>0); otherwise fall back to service default
      const custom = masterId ? Number(s?.staffDurationSeconds?.[masterId]) : NaN;
      const durSec = Number.isFinite(custom) && custom > 0 ? custom : Number(s?.duration_seconds ?? 0);
      const duration = formatDurationSeconds(durSec);
      // Last-hour offer applies to single-service bookings (README §3.2);
      // show the discounted price with the regular one struck through. For
      // range-priced services both ends are discounted — the admin settles
      // the final price at completion, as with any appointment.
      if (draft.lastHour && ids.length === 1) {
        const pct = draft.lastHour.discountPct;
        const pMin = shown.min;
        const pMax = shown.max;
        const hasRange = pMin > 0 && pMax > 0 && pMin !== pMax;
        const fmt = (v: number) =>
          new Intl.NumberFormat(language).format(Math.round(v * 100) / 100);
        const discounted = hasRange
          ? `${fmt(pMin * (1 - pct / 100))} - ${fmt(pMax * (1 - pct / 100))}`
          : fmt(draft.lastHour.price);
        const regular = hasRange ? `${fmt(pMin)} - ${fmt(pMax)}` : priceStr.replace(" " + currency, "");
        return {
          id,
          name: s.title,
          price: `${discounted} ${currency}`,
          oldPrice: `${regular} ${currency}`,
          lastHourPct: pct,
          duration,
        };
      }
      return { id, name: s.title, price: priceStr, duration };
    });
  }, [draft.serviceIds, draft.specialistId, draft.lastHour, snap, currency, language, t]);

  const dt = draft.startISO ? DateTime.fromISO(draft.startISO).setZone(tz) : null;
  const dateLabel = dt ? dt.setLocale(luxonLocale).toFormat("d LLLL yyyy") : "—";
  const timeLabel = dt ? dt.toFormat("HH:mm") : "—";

  // mutation
  const createRecordMutation = useCreatePublicRecord();
  const createRecord = createRecordMutation.mutate;
  const isLoading = createRecordMutation.status === "pending";
  const [errors, setErrors] = React.useState<{ name?: string; phone?: string; email?: string }>({});

  // Pre-populate contact fields from stored client auth (if logged in)
  React.useEffect(() => {
    const auth = getStoredClientAuth();
    if (auth) {
      // Only set if draft fields are empty (don't overwrite user input)
      if (!draft.contact?.phone && auth.phone) {
        actions.setClientPhone(auth.phone);
      }
      if (!draft.contact?.name && auth.name) {
        actions.setClientName(auth.name);
      }
    }
  }, []); // Run once on mount

  // Local status for the UI panel
  const [status, setStatus] = React.useState<StatusPanel | undefined>(undefined);

  const validate = () => {
    const e: typeof errors = {};
    // Shared contact validation (also used by the waiting-list panel) so the
    // two screens can never diverge — see @/lib/validation/contact.
    const nameErr = validateName(draft.contact?.name);
    if (nameErr) e.name = contactErrorMessage(nameErr, t);
    const phoneErr = validatePhone(draft.contact?.phone);
    if (phoneErr) e.phone = contactErrorMessage(phoneErr, t, {
      phoneExample: phoneCodeExample(companySettings?.phone_code),
    });
    const emailErr = validateOptionalEmail(draft.contact?.email);
    if (emailErr) e.email = contactErrorMessage(emailErr, t);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Translation/UX registry for backend errors
  const ERROR_MAP: Record<
    string,
    { message: string; button?: { label: string; onClick: () => void }; adjustDraft?: () => void }
  > = {
    "Selected time is no longer available": {
      message: t("widget.overview.time_unavailable", "The selected time is no longer available"),
      adjustDraft: () => {
        // keep date, clear concrete time only
        actions.setStartISO(null);
      },
      button: {
        label: t("widget.overview.choose_another_time", "Choose another time"),
        onClick: () => {
          onEditDateTime();
        },
      },
    },
  };

  const handleSubmit = () => {
    if (!validate()) return;

    // Clear previous status (do not touch the draft yet)
    setStatus(undefined);

    // send what the screen shows: no phone call offered → a messenger; a channel the
    // salon has switched off since → the fallback shown instead
    submitDraft({
      ...draft,
      confirmMethod: draft.confirmMethod === 2 || !phoneOffered ? 2 : 1,
      messengerChannel: pickedChannel?.id ?? draft.messengerChannel,
    });
  };

  const submitDraft = (d: typeof draft) => {
    createRecord(d, {
      onSuccess: (response: unknown) => {
        // 200 OK:
        //  - reset the draft
        //  - show success panel with action to go to the very first view
        //  - meta.messenger_link (client chose messenger, not yet bound) →
        //    the panel offers the one-tap Telegram connect
        const meta = (response as {
          data?: { meta?: { messenger_link?: string; personal_links?: PersonalLink[]; calendar?: unknown } };
        })?.data?.meta;
        const messengerLink = meta?.messenger_link;
        //  - meta.personal_links: "write to us" on the salon's own WhatsApp /
        //    Telegram / MAX, with the booking's code (inbox README §5.1)
        const personalLinks = Array.isArray(meta?.personal_links) ? meta.personal_links : undefined;
        //  - meta.calendar: Google / Outlook / .ics links for her own calendar
        //    (google-calendar README §9.7); absent → no "Add to calendar"
        const calendar = calendarLinksOf(meta?.calendar);
        // "Sat, 3 October · 12:00 · Salon" for the success screen — read
        // before the reset clears the draft
        const summary = dt
          ? [dt.setLocale(luxonLocale).toFormat("ccc, d LLLL"), timeLabel, companySettings?.title]
              .filter(Boolean)
              .join(" · ")
          : undefined;
        // what the client picked and the booking itself, for the booked screen
        const confirmMethod = d.confirmMethod === 2 || !phoneOffered ? 2 : 1;
        const picked = confirmMethod === 2 ? pickedChannel : undefined;
        const details = {
          service: services.map((s) => s.name).join(", ") || undefined,
          specialist: d.specialistId && d.specialistId !== "any" ? specialistName : undefined,
          when: dt ? `${dt.setLocale(luxonLocale).toFormat("ccc, d LLLL")} · ${timeLabel}` : undefined,
        };
        actions.reset();
        setStatus({
          type: "success",
          message: t("widget.overview.success", "Your booking has been created successfully"),
          actionLabel: t("widget.overview.create_new", "Create another booking"),
          onAction: onRestart,
          messengerLink,
          summary,
          personalLinks,
          confirmMethod,
          messengerChannel: picked?.id,
          messengerAuto: picked?.auto,
          messengerKind: picked?.kind,
          messengerLabel: picked?.label,
          details,
          calendar,
        });
      },
      onError: (err: unknown) => {
        // Expect FastAPI shape: { status: 'error', error: '...' }, status_code = 400
        const ax = err as import("axios").AxiosError<any>;

        // Demo salon: the backend read-only guard blocks real bookings with
        // 403 {"demo": true, ...} — show a friendly notice, keep the draft.
        if (ax?.response?.status === 403 && ax?.response?.data?.demo === true) {
          setStatus({
            type: "error",
            message: t(
              "demo.booking_disabled",
              "This is a demo salon — real bookings are disabled. Sign up to create your own.",
            ),
          });
          return;
        }

        // Last-hour offer expired (409, decision §6.3): the CLIENT decides —
        // re-offer the same slot at the regular price, or go pick another time.
        if (ax?.response?.status === 409 && ax?.response?.data?.code === "last_hour_expired") {
          actions.setLastHour(null);
          setStatus({
            type: "error",
            message: t(
              "widget.last_hour.expired",
              "The discount window for this time has passed — it can still be booked at the regular price.",
            ),
            actionLabel: t("widget.last_hour.book_regular", "Book at regular price"),
            onAction: () => {
              setStatus(undefined);
              submitDraft({ ...d, lastHour: null });
            },
          });
          return;
        }

        // Backend phone gate (400 invalid_phone): the number cannot exist
        // (libphonenumber) — anchor the error to the phone FIELD, localized,
        // instead of echoing the English backend string in the panel. Rarely
        // reached now that validatePhone runs the same rule pre-submit, but
        // the server stays authoritative (its metadata may be newer).
        if (ax?.response?.status === 400 && ax?.response?.data?.code === "invalid_phone") {
          const msg = contactErrorMessage("phone_invalid", t);
          setErrors((prev) => ({ ...prev, phone: msg }));
          setStatus({ type: "error", message: msg });
          return;
        }

        // Try to extract custom message; otherwise fall back to Error.message
        const raw =
          (ax?.response?.data && (ax.response.data.error || ax.response.data.message)) ||
          (ax?.message ?? "Unknown error");
        const known = ERROR_MAP[String(raw)];

        if (known?.adjustDraft) known.adjustDraft();

        setStatus({
          type: "error",
          message: known ? known.message : String(raw),
          actionLabel: known?.button?.label,
          onAction: known?.button?.onClick,
        });

        // Keep draft as-is (except potential time clear above) — do not reset
        // eslint-disable-next-line no-console
        console.error("[Overview] create booking failed:", raw, err);
      },
    });
  };

  // Only offer signup when client accounts are enabled and the user is not logged in.
  const isLoggedIn = isClientLoggedIn();
  const canCreateClientAccount = companySettings?.client_account_enabled === true;
  const handleOpenDashboard = !isLoggedIn && canCreateClientAccount ? onOpenClientDashboard : undefined;

  return (
    <OverviewView
      specialistName={specialistName}
      specialistAvatar={specialistAvatar}
      services={services}
      dateLabel={dateLabel}
      timeLabel={timeLabel}
      onBack={onBack}
      onEditSpecialist={onEditSpecialist}
      onEditServices={onEditServices}
      onEditDateTime={onEditDateTime}

      // Controlled contact binding
      name={draft.contact?.name ?? ""}
      phone={draft.contact?.phone ?? ""}
      email={draft.contact?.email ?? ""}
      comment={draft.contact?.comment ?? ""}
      confirmMethod={draft.confirmMethod === 2 || !phoneOffered ? 2 : 1}
      messengersEnabled={messengersOn}
      messengerChannels={messengerChannels}
      messengerChannel={pickedChannel?.id}
      phoneOffered={phoneOffered}
      onChangeMessengerChannel={actions.setMessengerChannel}
      smsOffered={Boolean(companySettings?.messenger?.sms)}
      smsConsent={Boolean(draft.smsConsent)}
      onChangeSmsConsent={actions.setSmsConsent}

      onChangeName={actions.setClientName}
      onChangePhone={actions.setClientPhone}
      onChangeEmail={actions.setClientEmail}
      onChangeComment={actions.setClientComment}
      onChangeConfirmMethod={actions.setConfirmMethod}

      errors={errors}
      onSubmit={handleSubmit}
      isSubmitting={isLoading}
      status={status}
      onOpenClientDashboard={handleOpenDashboard}
    />
  );
}
